import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import {
  GET_LOCATIONS_CYBER_MODULE,
  MARK_LOCATION_AS_UPDATED,
} from '../queries';
import { Location } from '../../../exposure/dependency/DependencyMatrix';
import { MarkLocationAsUpdatedInput } from '../types';
import { FETCH_LOCATION_BY_ID } from '../../../dataInputs/queries/locations';

type AffectedLocation = {
  name: string;
  url: string;
};

type LocationToRemove = { uuid: string }[];

type UseFetchSelectedLocationsResult = {
  findAllLocations: Location[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<Location[]>;
};

/**
 * Hook to fetch the selected IT applications for the enterprise level
 * @returns The selected IT applications for the enterprise level
 */
export const useFetchSelectedLocations = () => {
  const { data, loading, error, refetch } =
    useQuery<UseFetchSelectedLocationsResult>(GET_LOCATIONS_CYBER_MODULE);

  return {
    data: data?.findAllLocations,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook to handle the confirmation flow for the RemoveITApplicationModal.
 * Shows a confirmation modal when removing IT applications that have been affected.
 */
export const useRemoveLocationConfirmation = (locations: Location[]) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [affectedLocations, setAffectedLocations] = useState<
    AffectedLocation[]
  >([]);

  // Callback to execute when user confirms the removal
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

  /**
   * Checks if removing IT applications would affect any applications.
   * If yes, shows the confirmation modal. If no, proceeds with the update immediately.
   */
  const handleBeforeUpdate = (
    locationsBeingRemoved: LocationToRemove,
    onProceed: () => void,
  ) => {
    // Check if removing IT applications would affect any applications
    const { hasAffectedLocations } = checkForAffectedLocations(
      locations,
      locationsBeingRemoved,
    );

    if (hasAffectedLocations) {
      // Get the affected applications that have been updated
      const affectedLocations = locations.filter((location) => {
        // Check if this application's IT application is being removed
        const isBeingRemoved = locationsBeingRemoved.some(
          (item) => item.uuid === location.uuid,
        );

        // Check if the application has been affected (has a non-null percentage and is greater than 0)
        const hasBeenAffected = location.hasDependencies;

        // Include in result if both conditions are true: IT app is being removed AND application was updated
        return isBeingRemoved && hasBeenAffected;
      });

      // Transform the affected applications into the format needed for the modal
      const uniqueAffectedLocations = new Map<string, AffectedLocation>();
      affectedLocations.forEach((location) => {
        // Ensure workaround.itApplicationId is not null or undefined before using it as a map key
        if (location.uuid && !uniqueAffectedLocations.has(location.uuid)) {
          uniqueAffectedLocations.set(location.uuid, {
            name: location.locationName || `Location ${location.uuid}`,
            url: `/cyber/location-level/${location.uuid}/options`,
          });
        }
      });
      const transformedLocations = Array.from(uniqueAffectedLocations.values());

      setAffectedLocations(transformedLocations);
      setOnConfirm(() => onProceed);
      setModalOpen(true);
    } else {
      onProceed();
    }
  };

  /**
   * Called when user clicks "Remove Applications" in the modal.
   * Executes the stored proceed callback to complete the removal.
   */
  const handleConfirm = () => {
    onConfirm?.(); // Execute the proceed callback if it exists
    setModalOpen(false);
    setOnConfirm(null);
  };

  /**
   * Called when user clicks "Cancel" in the modal.
   * Closes the modal without executing the proceed callback.
   */
  const handleCancel = () => {
    setModalOpen(false);
    setOnConfirm(null);
  };

  return {
    isModalOpen,
    handleBeforeUpdate,
    affectedLocations,
    handleConfirm,
    handleCancel,
  };
};

type AffectedLocationWarningResult = {
  hasAffectedLocations: boolean;
  count: number;
};

/**
 * Checks if removing IT applications would result in losing updated applications
 * @param applications - Array of current applications
 * @param appsBeingRemoved - Array of IT applications that are being removed
 * @returns Object containing whether there are updated applications being removed and the count
 */
export const checkForAffectedLocations = (
  locations: Location[],
  locationsBeingRemoved: { uuid: string }[],
): AffectedLocationWarningResult => {
  // Convert the list of apps being removed into a Set for faster lookups
  const removedAppIds = new Set(locationsBeingRemoved.map((item) => item.uuid));

  // Find applications for items being removed that have been updated
  const affectedLocations = locations.filter(
    (location) =>
      removedAppIds.has(location.uuid) && // Location is being removed
      location.hasDependencies, // Location has dependencies
  );

  return {
    hasAffectedLocations: affectedLocations.length > 0,
    count: affectedLocations.length,
  };
};

/**
 * Hook to mark a location as updated
 *
 * @returns The function to mark a location as updated and the loading and error states
 */
export const useMarkLocationAsUpdated = () => {
  const [markAsUpdated, { loading, error }] = useMutation(
    MARK_LOCATION_AS_UPDATED,
  );

  const handleMarkAsUpdated = async (
    locationUuid: string,
    onSuccess: () => void,
  ) => {
    try {
      const input: MarkLocationAsUpdatedInput = {
        locationUuid,
      };

      await markAsUpdated({
        variables: {
          data: input,
        },
      });

      onSuccess();
    } catch (error) {
      console.error('Error marking location as updated:', error);
    }
  };

  return {
    handleMarkAsUpdated,
    loading,
    error,
  };
};

export const useFetchLocationById = (locationId: string) => {
  const { data, loading, error, refetch } = useQuery<{
    findLocationByUuid: Location;
  }>(FETCH_LOCATION_BY_ID, {
    variables: {
      uuid: locationId,
    },
  });

  return {
    data: data?.findLocationByUuid,
    loading,
    error,
    refetch,
  };
};
